import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthProvider";
import { useState } from "react";
import useCustomFormik from "../../custom-hooks/useCustomFormik";
import { Link } from "react-router-dom";
import HomeButtonSVG from "../shared/svg/HomeButtonSVG";
import TogglePassword from "../shared/form-inputs/TogglePassword";
import { userRegisterFields, UserRegisterSchemaFactory } from "../../validation-schemas/UserRegisterSchema";
import CustomInput from "../../custom-components/form-inputs/CustomInput";
import LangSelect from "../header/LangSelect";
import PhoneInputComponent from "../shared/form-inputs/PhoneInput";
import { useErrorHandler } from "../../custom-hooks/useErrorHandler";
import { StatusCodes } from "http-status-codes";
import CustomError from "../../custom-components/form-inputs/CustomError";
import Toast from "../../custom-components/Toast";
import { defaultResponseMessageObj, ResponseMessageObj } from "../../types/ApiResults";
import { ResponseMessage } from "../../domain/enums/common/ResponseMessageEnum";
import { v4 as uuidv4 } from "uuid";

export default function RegisterPage() {
    const [isPasswordVisible, setPasswordVisibility] = useState<boolean>(false);
    const { errorKey, setErrorKey, resetErrors, isError } = useErrorHandler(StatusCodes.BAD_REQUEST);
    const [messageObj, setMessageObj] = useState<ResponseMessageObj>(defaultResponseMessageObj);
    const { register } = useAuth();
    const { t } = useTranslation();

    const { form } = useCustomFormik({
        initial: null,
        fields: userRegisterFields,
        schema: UserRegisterSchemaFactory(t),
        onSubmit: async (values, actions) => {
            actions.setSubmitting(true);
            resetErrors();

            if (!values.email || !values.password || !values.firstName || !values.lastName) {
                return;
            }
            if (values.password !== values.confirmPassword) {
                setErrorKey("errors.validation.password.passwordsMustMatch");
                return;
            }

            console.log("Registering user with values:", values);

            try {
                await register(values);
                if (!isError()) {
                    actions.resetForm();
                    setMessageObj({
                        id: uuidv4(),
                        messageType: ResponseMessage.SUCCESS,
                        messageKey: t("auth.register.success"),
                    });
                }
            } catch (err) {
                setErrorKey("errors.common");
                setMessageObj({
                    id: uuidv4(),
                    messageType: ResponseMessage.DANGER,
                    messageKey: t("errors.common"),
                });
                return;
            }
        },
    });

    const resetMessageObj = () => {
        setMessageObj(defaultResponseMessageObj);
    };

    return (
        <div className="flex items-center justify-center h-[100vh]">
            <div className="max-w-sm bg-gray-200 px-[24px] py-[24px] rounded-xl">
                <div className="flex items-center justify-between w-[400px]">
                    <Link to={"/"}>
                        <HomeButtonSVG fill="#000" />
                    </Link>
                    <LangSelect
                        additionalButtonClassName="absolute z-10 right-[65px]"
                        additionalPopupClassName="absolute z-10 right-[65px] top-[10px] bg-gray-100 shadow-md"
                    />
                </div>
                <form id="enterForm" onSubmit={form.handleSubmit}>
                    <h1 className="block text-center mt-4 mb-4 text-[22px] font-normal text-gray-700">
                        {t("auth.register.title")}
                    </h1>

                    <CustomInput
                        type="email"
                        labelKey="auth.fields.email"
                        name="email"
                        placeholderKey="auth.fields.email"
                        errorKey={form.errors.email}
                        value={form.values.email}
                        onChange={form.handleChange}
                        className={`${form.touched.email && form.errors.email && "bg-red-100"}`}
                    />

                    <div className="flex items-center justify-between">
                        <div className="flex-1 mr-4">
                            <CustomInput
                                type={isPasswordVisible ? "text" : "password"}
                                labelKey="auth.fields.password"
                                name="password"
                                placeholderKey="auth.fields.password"
                                errorKey={form.errors.password}
                                value={form.values.password}
                                onChange={form.handleChange}
                                className={` ${form.touched.password && form.errors.password && "bg-red-100"}`}
                            />
                        </div>

                        <TogglePassword
                            isPasswordVisible={isPasswordVisible}
                            setPasswordVisible={setPasswordVisibility}
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex-1 mr-4">
                            <CustomInput
                                type={isPasswordVisible ? "text" : "password"}
                                labelKey="auth.fields.confirmPassword"
                                name="confirmPassword"
                                placeholderKey="auth.fields.confirmPassword"
                                errorKey={form.errors.confirmPassword}
                                value={form.values.confirmPassword}
                                onChange={form.handleChange}
                                className={` ${
                                    form.touched.confirmPassword && form.errors.confirmPassword && "bg-red-100"
                                }`}
                            />
                        </div>

                        <TogglePassword
                            isPasswordVisible={isPasswordVisible}
                            setPasswordVisible={setPasswordVisibility}
                        />
                    </div>

                    <CustomInput
                        type="text"
                        labelKey="auth.fields.firstName"
                        name="firstName"
                        placeholderKey="auth.fields.firstName"
                        errorKey={form.errors.firstName}
                        value={form.values.firstName}
                        onChange={form.handleChange}
                        className={`${form.touched.firstName && form.errors.firstName && "bg-red-100"}`}
                    />

                    <CustomInput
                        type="text"
                        labelKey="auth.fields.lastName"
                        name="lastName"
                        placeholderKey="auth.fields.lastName"
                        errorKey={form.errors.lastName}
                        value={form.values.lastName}
                        onChange={form.handleChange}
                        className={`${form.touched.lastName && form.errors.lastName && "bg-red-100"}`}
                    />

                    <CustomInput
                        type="text"
                        labelKey="auth.fields.middleName"
                        name="middleName"
                        placeholderKey="auth.fields.middleName"
                        errorKey={form.errors.middleName}
                        value={form.values.middleName}
                        onChange={form.handleChange}
                        className={`${form.touched.middleName && form.errors.middleName && "bg-red-100"}`}
                    />

                    <PhoneInputComponent
                        meta={userRegisterFields.phone}
                        field="phone"
                        value={form.values.phone}
                        errorKey={form.errors.phone}
                        onChange={(value) => form.setFieldValue("phone", value)}
                    />

                    <button
                        className="flex items-center text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-4 py-2.5 text-center h-[42px]"
                        type="submit"
                    >
                        {t("auth.actions.register")}
                    </button>

                    <CustomError errorKey={errorKey} name={errorKey} />
                </form>
            </div>
            <Toast messageObj={messageObj} resetMessageObj={resetMessageObj} />
        </div>
    );
}
